import { forwardRef, useCallback, useState } from "react";

import * as RadixTooltip from "@radix-ui/react-tooltip";
import { type VariantProps, cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

const tooltip = cva(
  "z-[70] max-w-[320px] text-pretty rounded bg-gray-600 px-2 py-1 text-center text-xs text-white",
);

const arrow = cva("fill-gray-600");

export type Side = RadixTooltip.TooltipContentProps["side"];

type RadixTooltipContentElement = React.ElementRef<typeof RadixTooltip.Content>;
type TooltipVariantProps = VariantProps<typeof tooltip>;

export type Props = TooltipVariantProps &
  Omit<React.ComponentProps<typeof RadixTooltip.Content>, "content"> & {
    content: React.ReactNode;
    isOpen?: boolean;
    isOpenDefault?: boolean;
    /**
     * Delay in milliseconds before the tooltip appears
     */
    delay?: number;
    /**
     * Renders children directly without a tooltip when true.
     */
    isDisabled?: boolean;
    children?: React.ReactNode;
    className?: string;
    classNames?: {
      arrow?: string;
    };
    onOpenChange?: (isOpen: boolean) => void;
    side?: Side;
  };

export const Tooltip = forwardRef<RadixTooltipContentElement, Props>(
  (
    {
      content,
      isOpen,
      isOpenDefault,
      delay = 500,
      isDisabled = false,
      children,
      className,
      classNames,
      onOpenChange,
      side = "bottom",
      ...rest
    },
    ref,
  ) => {
    const [_isOpen, _setIsOpen] = useState(isOpenDefault ?? false);

    const handleOpenChange = useCallback(
      (value: boolean) => {
        if (isOpen === undefined) {
          _setIsOpen(value);
        }
        onOpenChange?.(value);
      },
      [isOpen, onOpenChange],
    );

    if (isDisabled) {
      return children;
    }

    return (
      <RadixTooltip.Provider delayDuration={delay}>
        <RadixTooltip.Root
          open={isOpen}
          defaultOpen={isOpenDefault}
          onOpenChange={handleOpenChange}
        >
          <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
          {/* AnimatePresence causes the tooltip to get stuck and not close if a user scrolls while the tooltip is open */}
          {/* <AnimatePresence> */}
          {(isOpen ?? _isOpen) && (
            <RadixTooltip.Portal forceMount>
              <RadixTooltip.Content
                sideOffset={4}
                collisionPadding={10}
                asChild
                ref={ref}
                className={twMerge(
                  tooltip({
                    className,
                  }),
                )}
                side={side}
                {...rest}
              >
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={{
                    hidden: { opacity: 0, scale: 0.95 },
                    visible: { opacity: 1, scale: 1 },
                  }}
                  transition={{ duration: 0.075 }}
                >
                  {content}
                  <RadixTooltip.Arrow
                    className={twMerge(
                      arrow({
                        className: classNames?.arrow,
                      }),
                    )}
                  />
                </motion.div>
              </RadixTooltip.Content>
            </RadixTooltip.Portal>
          )}
          {/* </AnimatePresence> */}
        </RadixTooltip.Root>
      </RadixTooltip.Provider>
    );
  },
);

Tooltip.displayName = "Tooltip";
